((root, factory) => {
  const core = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = core;
  }

  if (root) {
    root.PetMealsOrderBuilderCore = core;
  }
})(typeof window !== 'undefined' ? window : globalThis, () => {
  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function getOptionIndex(product, matcher, fallbackIndex) {
    const options = Array.isArray(product?.options) ? product.options : [];
    const index = options.findIndex((option) => matcher.test(String(option || '')));
    return index >= 0 ? index : fallbackIndex;
  }

  function getPackOptionIndex(product) {
    return getOptionIndex(product, /pack/i, 0);
  }

  function getDeliveryPeriodOptionIndex(product) {
    return getOptionIndex(product, /delivery\s*period/i, 1);
  }

  function resolveVariant(product, packValue, deliveryPeriod, availableOnly = true) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const packIndex = getPackOptionIndex(product);
    const periodIndex = getDeliveryPeriodOptionIndex(product);
    const targetPack = normalize(packValue);
    const targetPeriod = normalize(deliveryPeriod);

    return (
      variants.find((variant) => {
        const options = Array.isArray(variant?.options) ? variant.options : [];
        if (availableOnly && variant?.available === false) return false;
        if (targetPack && normalize(options[packIndex]) !== targetPack) return false;
        if (targetPeriod && normalize(options[periodIndex]) !== targetPeriod) return false;
        return true;
      }) || null
    );
  }

  function getPackCount(packValue) {
    const match = String(packValue || '').match(/\d+/);
    const count = match ? Number(match[0]) : 1;
    return Number.isFinite(count) && count > 0 ? count : 1;
  }

  function uniqueStrings(values) {
    return Array.from(
      new Set(
        (values || [])
          .map((value) => String(value || '').trim())
          .filter(Boolean)
      )
    );
  }

  function isShippingFeeItem(item) {
    return item?.properties?._shipfee === 'true';
  }

  function getMealDeliveryDate(item) {
    return String(item?.properties?.['Selected Consumption Dates'] || '').trim();
  }

  function getItemDeliveryPeriod(item) {
    const propertyValue = item?.properties?.['Delivery Period'];
    if (propertyValue) return String(propertyValue).trim();

    const option = (item?.options_with_values || []).find((currentOption) =>
      /delivery\s*period/i.test(String(currentOption?.name || ''))
    );
    if (option?.value) return String(option.value).trim();

    return (
      (item?.variant_options || []).find((value) =>
        /same\s*day|day[\s-]*before/i.test(String(value || ''))
      ) || ''
    );
  }

  function getExistingShippingDates(cart) {
    return new Set(
      (cart?.items || [])
        .filter(isShippingFeeItem)
        .map((item) => String(item.properties?.['Delivery Date'] || '').trim())
        .filter(Boolean)
    );
  }

  function sameStringSet(leftValues, rightValues) {
    const left = uniqueStrings(leftValues).sort();
    const right = uniqueStrings(rightValues).sort();
    return left.length === right.length && left.every((value, index) => value === right[index]);
  }

  function getCartCompatibilityError(cart, { dates, areaSlug, deliveryPeriod }) {
    const mealItems = (cart?.items || []).filter(
      (item) => !isShippingFeeItem(item) && getMealDeliveryDate(item)
    );
    if (mealItems.length === 0) return '';

    const existingDates = mealItems.map(getMealDeliveryDate);
    if (!sameStringSet(existingDates, dates)) {
      return 'schedule';
    }

    const existingAreas = uniqueStrings(
      mealItems.map((item) => item.properties?.['Delivery Location'])
    );
    if (existingAreas.length > 0 && !existingAreas.every((area) => area === areaSlug)) {
      return 'area';
    }

    const existingPeriods = uniqueStrings(mealItems.map(getItemDeliveryPeriod));
    if (
      existingPeriods.length > 0 &&
      !existingPeriods.every((period) => normalize(period) === normalize(deliveryPeriod))
    ) {
      return 'period';
    }

    return '';
  }

  function buildOrderPayload({
    selections,
    dates,
    area,
    furbabyName,
    deliveryPeriod,
    shippingFeeVariantId,
    shippingFeeLabel,
    existingShippingDates = new Set(),
  }) {
    const normalizedSelections = (selections || []).filter(
      (selection) => Number(selection?.quantity) > 0 && selection?.variantId
    );
    const normalizedDates = uniqueStrings(dates);
    const feeDates = new Set(existingShippingDates);
    const items = [];
    const addedShippingFeeDates = [];
    const linkedProducts = uniqueStrings(
      normalizedSelections.map((selection) => selection.productTitle)
    ).join(', ');

    normalizedDates.forEach((deliveryDate) => {
      normalizedSelections.forEach((selection) => {
        items.push({
          id: selection.variantId,
          quantity: Math.max(1, Number(selection.quantity) || 1),
          properties: {
            'Furbaby Name': furbabyName,
            'Selected Consumption Dates': deliveryDate,
            'Delivery Period': deliveryPeriod,
            'Delivery Location': area.slug,
            'Delivery Location Name': area.name,
            'Shipping Fee': shippingFeeLabel,
          },
        });
      });

      if (!feeDates.has(deliveryDate)) {
        items.push({
          id: shippingFeeVariantId,
          quantity: 1,
          properties: {
            _shipfee: 'true',
            'Furbaby Name': furbabyName,
            'Delivery Date': deliveryDate,
            'Delivery Period': deliveryPeriod,
            'Delivery Location': area.slug,
            'Delivery Location Name': area.name,
            'Delivery Fee': shippingFeeLabel,
            'Linked Products': linkedProducts || 'Pet Meals',
          },
        });
        feeDates.add(deliveryDate);
        addedShippingFeeDates.push(deliveryDate);
      }
    });

    return {
      items,
      addedShippingFeeDates,
      mealLineCount: normalizedSelections.length * normalizedDates.length,
    };
  }

  function getOrderTotals({ selections, dateCount, shippingFeeCents }) {
    const deliveryCount = Math.max(0, Number(dateCount) || 0);
    const selected = (selections || []).filter((selection) => Number(selection?.quantity) > 0);
    const packsPerDelivery = selected.reduce(
      (total, selection) =>
        total + getPackCount(selection.packValue) * Math.max(1, Number(selection.quantity) || 1),
      0
    );
    const mealSubtotalCents = selected.reduce(
      (total, selection) =>
        total + Math.max(0, Number(selection.price) || 0) * Math.max(1, Number(selection.quantity) || 1),
      0
    ) * deliveryCount;
    const deliveryTotalCents = Math.max(0, Number(shippingFeeCents) || 0) * deliveryCount;

    return {
      recipeCount: selected.length,
      packsPerDelivery,
      totalPacks: packsPerDelivery * deliveryCount,
      mealSubtotalCents,
      deliveryTotalCents,
      grandTotalCents: mealSubtotalCents + deliveryTotalCents,
    };
  }

  return Object.freeze({
    buildOrderPayload,
    getCartCompatibilityError,
    getDeliveryPeriodOptionIndex,
    getExistingShippingDates,
    getOrderTotals,
    getPackCount,
    getPackOptionIndex,
    resolveVariant,
    sameStringSet,
  });
});
